import { useMemo, useState } from 'react';
import { mapPoiItem } from '../utils/mapPoiItem';
import { IPoi } from '../../../domain/models/IPoiItem';
import { PoiListScope } from '../models/PoiListScope';
import { formatLocationForUrl } from '../utils/formatLocationForUrl';
import { IDropdownItem } from '../../../../../ui';
import { IPoiLocation, POI_URL_PARAM_ALL } from '../models/POILocations';
import { useMeridianData } from '../../../../../common-data/useMeridianData';
import { ILocationResponseItem } from '../../../../../common-data/models/ILocationResponseItem';
import { ICategoryResponseItem } from '../../../../../common-data/models/ICategoryResponseItem';
import { sortAlphabetically } from '../../../../../utils/sortAlphabetically';

export type PoiStore = ReturnType<typeof usePoiStore>;

export function usePoiStore(filterCategories?: string[]) {
	const [scope, setScope] = useState<PoiListScope>(PoiListScope.All);
	const [searchQuery, setSearchQuery] = useState<string | undefined>('');
	const [location, setLocation] = useState<string | undefined>();

	const {
		placemarks,
		placemarksLoading,
		placemarksError,
		locations,
		locationsLoading,
		locationsError,
		categories,
		categoriesLoading,
		categoriesError,
	} = useMeridianData();

	const allLocationsMap: { [key: string]: ILocationResponseItem } = useMemo(
		() => Object.fromEntries(locations.map((item) => [item.id, item])),
		[locations],
	);

	const poiCategories = useMemo(() => {
		if (!filterCategories) return categories;
		return categories.filter((item) => filterCategories.includes(item.name));
	}, [filterCategories, categories]);

	const poiCategoriesMap: { [key: string]: ICategoryResponseItem } = useMemo(
		() => Object.fromEntries(poiCategories.map((item) => [item.id, item])),
		[poiCategories],
	);

	const poiItems: IPoi[] = useMemo(
		() =>
			placemarks
				.filter((item) =>
					!filterCategories // if there are no filterCategories, do not filter by poiCategoriesMap
						? true
						: item.categoryIds.split(',').some((id) => id in poiCategoriesMap),
				)
				.map((item) => mapPoiItem(item, poiCategoriesMap, allLocationsMap))
				.sort((a, b) => sortAlphabetically(a.title, b.title)),
		[poiCategoriesMap, placemarks, allLocationsMap],
	);

	const poiLocations: IPoiLocation[] = useMemo(() => {
		const locationsList = poiItems.map((poi) => poi.location);
		const uniqueLocations = [...new Set(locationsList)].filter((item) => item);
		return uniqueLocations
			.map((item) => ({
				key: formatLocationForUrl(item as IDropdownItem),
				value: item,
			}))
			.sort((a, b) => a.value?.localeCompare(b.value || '') || 0);
	}, [poiItems]);

	const filteredData: IPoi[] = useMemo(() => {
		let filteredPoiData = poiItems;

		if (scope !== POI_URL_PARAM_ALL) {
			filteredPoiData = filteredPoiData.filter(
				(poi) => poi.type.toLowerCase() === scope,
			);
		}
		if (location && location !== POI_URL_PARAM_ALL) {
			filteredPoiData = filteredPoiData.filter(
				(poi) =>
					poi.location && formatLocationForUrl(poi.location) === location,
			);
		}

		if (searchQuery) {
			filteredPoiData = filteredPoiData.filter((poi) => {
				const poiStringData = [poi.title, poi.keywords, poi.type, poi.typeName]
					.filter((data) => data) // ensure data is not null or empty
					.join(' ');
				return poiStringData.toLowerCase().includes(searchQuery.toLowerCase());
			});
		}

		return filteredPoiData;
	}, [poiItems, scope, location, searchQuery]);

	return {
		data: {
			loading: placemarksLoading || locationsLoading || categoriesLoading,
			error: placemarksError || locationsError || categoriesError,
			filteredData,
			poiLocations,
		},
		scope,
		setScope,
		setSearchQuery,
		setLocation,
	};
}
